import React, { useState, useEffect } from 'react';
import { BookOpen, FileText, GraduationCap, Calendar, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import StudentLessonsPortal from './StudentLessonsPortal';
import StudentHomeworkPortal from './StudentHomeworkPortal';

const StudentPortal = () => {
  const [activeTab, setActiveTab] = useState('lessons');
  const [stats, setStats] = useState({
    totalLessons: 0,
    upcomingLessons: 0,
    totalHomework: 0,
    pendingHomework: 0
  });
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, isStudent, loading: authLoading } = useAuth();

  useEffect(() => {
    if (isAuthenticated && isStudent && !authLoading) {
      fetchStats();
    }
  }, [isAuthenticated, isStudent, authLoading]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      // Fetch lessons and homework in parallel
      const [lessonsResponse, homeworkResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/lessons/student/they`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/homework/student/they`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const lessons = lessonsResponse.ok ? await lessonsResponse.json() : [];
      const homework = homeworkResponse.ok ? await homeworkResponse.json() : [];

      const upcomingLessons = lessons.filter(lesson => 
        lesson.status?.toLowerCase() === 'upcoming'
      ).length;

      const pendingHomework = homework.filter(hw => 
        hw.status === 'Assigned' || hw.status === 'In Progress'
      ).length;

      setStats({
        totalLessons: lessons.length,
        upcomingLessons,
        totalHomework: homework.length,
        pendingHomework
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    {
      id: 'lessons',
      name: 'My Lessons',
      icon: BookOpen,
      component: StudentLessonsPortal,
      count: stats.totalLessons,
      color: 'blue'
    },
    {
      id: 'homework',
      name: 'My Homework',
      icon: FileText,
      component: StudentHomeworkPortal,
      count: stats.totalHomework,
      color: 'green'
    }
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  const getColorClasses = (color, isActive) => {
    const colors = {
      blue: isActive ? 'border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300',
      green: isActive ? 'border-green-500 text-green-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
    };
    return colors[color] || colors.blue;
  };

  const getIconColor = (color, isActive) => {
    const colors = {
      blue: isActive ? 'text-blue-500' : 'text-gray-400',
      green: isActive ? 'text-green-500' : 'text-gray-400'
    };
    return colors[color] || colors.blue;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading student portal...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !isStudent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md mx-auto text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600 mb-4">
            {!isAuthenticated ? 'Please log in to access the student portal.' : 'This portal is only available to students.'}
          </p>
          <button
            onClick={() => window.location.href = '/login'}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            {!isAuthenticated ? 'Go to Login' : 'Go to Dashboard'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Header */}
          <div className="py-6">
            <div className="flex items-center gap-3 mb-2">
              <GraduationCap className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Student Portal</h1>
                <p className="text-gray-600">Manage your lessons and homework assignments</p>
              </div>
            </div>
          </div>

          {/* Stats Overview */}
          {!loading && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-600">Total Lessons</p>
                    <p className="text-2xl font-bold text-blue-900">{stats.totalLessons}</p>
                  </div>
                  <BookOpen className="w-8 h-8 text-blue-500" />
                </div>
              </div>
              
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-600">Upcoming</p>
                    <p className="text-2xl font-bold text-orange-900">{stats.upcomingLessons}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-orange-500" />
                </div>
              </div>
              
              <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600">Total Homework</p>
                    <p className="text-2xl font-bold text-green-900">{stats.totalHomework}</p>
                  </div>
                  <FileText className="w-8 h-8 text-green-500" />
                </div>
              </div>
              
              <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-red-600">Pending</p>
                    <p className="text-2xl font-bold text-red-900">{stats.pendingHomework}</p>
                  </div>
                  <Clock className="w-8 h-8 text-red-500" />
                </div>
              </div>
            </div>
          )}

          {/* Tabs Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                      ${getColorClasses(tab.color, isActive)}
                      transition-colors duration-200 relative
                    `}
                  >
                    <Icon className={`
                      w-5 h-5 mr-2
                      ${getIconColor(tab.color, isActive)}
                    `} />
                    {tab.name}
                    {tab.count > 0 && (
                      <span className={`
                        ml-2 py-0.5 px-2 text-xs rounded-full
                        ${isActive 
                          ? 'bg-blue-100 text-blue-600' 
                          : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                        }
                      `}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {ActiveComponent && <ActiveComponent />}
      </div>

      {/* Mobile Tabs */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
        <div className="flex">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex-1 flex flex-col items-center py-3 px-2 text-xs font-medium
                  ${isActive ? 'text-blue-600 bg-blue-50' : 'text-gray-500'}
                  transition-colors duration-200 relative
                `}
              >
                <Icon className={`
                  w-5 h-5 mb-1
                  ${isActive ? 'text-blue-500' : 'text-gray-400'}
                `} />
                <span className="text-xs">{tab.name}</span>
                {tab.count > 0 && (
                  <span className={`
                    absolute top-2 right-4 w-5 h-5 text-xs rounded-full flex items-center justify-center
                    ${isActive ? 'bg-blue-500 text-white' : 'bg-gray-400 text-white'}
                  `}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Add padding for mobile bottom navigation */}
      <div className="lg:hidden pb-20"></div>
    </div>
  );
};

export default StudentPortal;